import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigation, useNavigate } from 'react-router-dom';
import axios from '../../helpers/custom-axios';
import SubIndexControls from './SubIndexControls';
import PaginateControls from './table-components/PaginateControls';
import SubmissionTable from './table-components/SubmissionTable';
import Icon from '../common/Icon';
import { QueryContext, FilterFieldsContext } from '../../hooks/Contexts';
import {
  deletePreference,
  getPreference,
  setPreference,
} from '../../helpers/localStorageMethods';

const SubmissionsIndex = ({
  form,
  informerField,
  reloadFormQuotaData,
  setFilterByCustom,
}) => {
  const nav = useNavigation();
  const navigate = useNavigate();
  const { filtFields, filterByCustom, customFiltersAvailable } =
    useContext(FilterFieldsContext);
  const filterByCustomRef = useRef();
  const [submissions, setSubmissions] = useState([]);
  const [meta, setMeta] = useState({ total_records: '', filters: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [queryParams, setQueryParams] = useState(() => {
    if (history.state?.name === 'submissions' && history.state?.query) {
      let encoded = Object.fromEntries(
        new URLSearchParams(history.state.query)
      );
      if (encoded.filters) {
        encoded.filters = JSON.parse(encoded.filters);
      }
      return encoded;
    } else
      return {
        page: '1',
        sort: 'id.desc',
        filters: {},
        course_code: '',
        per_page: '25',
        filter_type: filterByCustom ? 'custom' : 'default',
      };
  });
  const hasLocalQuota = Object.keys(informerField).length ? true : false;
  const expandable = filtFields.length > 4;
  const filteringByExpanded =
    expandable &&
    filtFields
      .slice(4)
      .some((field) => Object.keys(meta.filters).includes(String(field.id)));

  useEffect(() => {
    getSubmissions();
    if (!Object.keys(queryParams.filters).length) {
      history.replaceState({ query: '' }, '');
    }
  }, [queryParams]);

  useEffect(() => {
    if (nav.state === 'loading') replaceHistoryParams();
    let main = document.getElementsByTagName('main')[0];
    let storedExpandedState = getPreference(`exp-${form.id}`);
    let widths = { normal: '1240px', wide: '1880px' };
    if (expandable && Number(storedExpandedState) === 1) {
      main.style.maxWidth = widths.wide;
      setExpanded(true);
    } else {
      main.style.maxWidth = widths.normal;
      setExpanded(false);
      if (storedExpandedState) deletePreference(`exp-${form.id}`);
    }
  }, [nav.state, filterByCustom]);

  useEffect(() => {
    // Handles the reloading of subs when filter_fields change
    // Prevents loading subs twice on initial load by delaying the
    //  assignment of filterByCustumRef.current, thereby only catching
    //  actual changes caused by toggling filterByCustom
    if (
      filterByCustomRef.current != undefined &&
      filterByCustom != filterByCustomRef.current
    ) {
      setQueryParams((queryParams) => {
        return {
          per_page: queryParams.per_page,
          page: 1,
          sort: 'id.desc',
          filters: {},
          course_code: '',
          filter_type: filterByCustom ? 'custom' : 'default',
        };
      });
    }
    filterByCustomRef.current = filterByCustom;
  }, [filterByCustom]);

  // Resets width & transition styling if filters were expanded...
  useEffect(() => {
    return () => {
      let main = document.getElementsByTagName('main')[0];
      main.style.transition = '';
      main.style.maxWidth = '1240px';
    };
  }, []);

  const generateSearchParams = (params) => {
    let filters = params.filters ? JSON.stringify(params.filters) : '';

    let query = new URLSearchParams({
      ...params,
      filters,
    }).toString();
    return query;
  };

  const replaceHistoryParams = () => {
    const query = generateSearchParams(queryParams);
    history.replaceState({ name: 'submissions', query }, '');
  };

  const clearFilters = () => {
    setQueryParams({
      ...queryParams,
      page: 1,
      sort: 'id.desc',
      filters: {},
      course_code: '',
    });
  };

  const handleToggleCustom = () => {
    // clear submissions because responses aren't loaded yet, and React gets angry about that
    setSubmissions([]);
    filterByCustom
      ? deletePreference(`custom-${form.id}`)
      : setPreference(`custom-${form.id}`, '1');
    setFilterByCustom(!filterByCustom);
  };

  const getSubmissions = () => {
    setIsLoading(true);
    const newSearchParams = generateSearchParams(queryParams);

    axios
      .get(`/manage/submissions?${newSearchParams}`, {
        params: { form_id: form.id },
      })
      .then((res) => {
        setSubmissions(res.data.submissions);
        setMeta(res.data.meta);
        if (form.global_quota) {
          reloadFormQuotaData();
        }
        setIsLoading(false);
      })
      .catch((error) => console.error(error));
  };

  const handleExpand = () => {
    let main = document.getElementsByTagName('main')[0];
    main.style.transition = 'all 0.3s ease-in-out';
    let width = expanded ? '1240px' : '1880px';
    expanded
      ? deletePreference(`exp-${form.id}`)
      : setPreference(`exp-${form.id}`, '1');
    setExpanded(!expanded);
    main.style.maxWidth = width;
  };

  return (
    <>
      <h4 className='custom-filters'>
        <span>
          Customized Filters
          {customFiltersAvailable && (
            <Icon
              icon={filterByCustom ? 'toggle-on fa-lg' : 'toggle-off fa-lg'}
              onClick={handleToggleCustom}
              title={
                filterByCustom
                  ? 'Restore Default Filters'
                  : 'Use Custom Filters'
              }
            />
          )}
          <Icon
            icon='gear'
            title='Edit Custom Filter Fields'
            onClick={() => navigate(`/manage/forms/${form.id}/edit`)}
          />
        </span>
      </h4>
      <section className='submissions'>
        <QueryContext.Provider
          value={{
            queryParams,
            setQueryParams,
            expandable,
            expanded,
            handleExpand,
            filteringByExpanded,
          }}
        >
          <SubIndexControls
            form={form}
            clearFilters={clearFilters}
            meta={meta}
            hasLocalQuota={hasLocalQuota}
            handleToggleCustom={handleToggleCustom}
          />
          <div className='table-wrapper'>
            <SubmissionTable
              submissions={submissions}
              isLoading={isLoading}
              hasLocalQuota={hasLocalQuota}
            />
          </div>
          {!isLoading && <PaginateControls totalPages={meta.total_pages} />}
        </QueryContext.Provider>
      </section>
    </>
  );
};

export default SubmissionsIndex;
